import classNames from "classnames";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import type { ISuggestion } from "@repo/shopify/hooks/useGooglePlacesAutocomplete";

import Button from "@atoms/Button";

/** 화면 표시에 필요한 필드만 요구해서 Google Places 외 데이터(JP 우편번호 등)도 사용 가능 */
type TSuggestionItem = Pick<ISuggestion, "placeId" | "text" | "matches">;

interface IProps<T extends TSuggestionItem> {
  suggestions: T[];
  onSelect: (suggestion: T) => () => void | Promise<void>;
  className?: string;
}

export default function AddressSuggestions<T extends TSuggestionItem>({ suggestions, onSelect, className }: IProps<T>) {
  const [focusedIndex, setFocusedIndex] = useState(-1);

  useEffect(() => {
    setFocusedIndex(-1);
  }, [suggestions]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (suggestions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
          break;
        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case "Enter":
          if (focusedIndex >= 0 && focusedIndex < suggestions.length) {
            e.preventDefault();
            onSelect(suggestions[focusedIndex])();
          }
          break;
      }
    },
    [suggestions, focusedIndex, onSelect],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <ul className={classNames("absolute top-full z-40 flex w-full flex-col justify-start gap-5 bg-white p-2.5 shadow", className)}>
      {suggestions.map((suggestion, index) => {
        const { placeId, text, matches } = suggestion;

        return (
          <li
            className={classNames("scrollbar-hide w-full shrink-0 overflow-x-auto", {
              "bg-[#f5f5f5] pl-1": index === focusedIndex,
            })}
            key={placeId}
          >
            <Button
              as="text"
              className="w-max min-w-full text-left whitespace-nowrap"
              onClick={onSelect(suggestion)}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
            >
              <HighlightedText matches={matches} text={text} />
            </Button>
          </li>
        );
      })}
    </ul>
  );
}

function HighlightedText({ text, matches }: { text: string; matches: google.maps.places.StringRange[] }) {
  const parts = useMemo(() => {
    if (!matches || matches.length === 0) {
      return [{ text, isHighlighted: false }];
    }

    const sortedMatches = [...matches].sort((a, b) => a.startOffset - b.startOffset);

    const segments = sortedMatches.flatMap(({ startOffset, endOffset }, index) => {
      // 이전 매칭이 끝난 위치 계산
      const prevEndOffset = index === 0 ? 0 : sortedMatches[index - 1].endOffset;

      const result: { text: string; isHighlighted: boolean }[] = [];

      //  이전 매칭과 현재 매칭 사이에 빈 공간이 있는지 확인
      if (prevEndOffset < startOffset) {
        // 사이 공간이 있으면 일반텍스트로 추가
        result.push({
          text: text.slice(prevEndOffset, startOffset),
          isHighlighted: false,
        });
      }

      // 현재 매칭 부분을 하이라이트로 추가
      result.push({
        text: text.slice(startOffset, endOffset),
        isHighlighted: true,
      });

      return result;
    });

    // 마지막 매칭 이후 남은 텍스트 처리
    const lastEndOffset = sortedMatches[sortedMatches.length - 1].endOffset;
    if (lastEndOffset < text.length) {
      segments.push({
        text: text.slice(lastEndOffset),
        isHighlighted: false,
      });
    }

    return segments;
  }, [text, matches]);

  return (
    <>
      {parts.map((part, index) => (
        <span className={part.isHighlighted ? "font-bold text-black" : "text-grey"} key={index}>
          {part.text}
        </span>
      ))}
    </>
  );
}
